import React, { useState, useEffect, useRef } from 'react';
import { Map, MapPin, ChevronDown, ChevronUp, Database, Layers, BarChart3, X } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth0 } from '@auth0/auth0-react';
import ApiManager from '../services/api/ApiManager';

function MapResults({ results, isVisible, viewType = 'state', searchOrigin, filters = {}, colorMetric = 'load_count', colorStyle = 'red-orange', colorScale = 'linear' }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showRoutes, setShowRoutes] = useState(true);
  // Use viewType prop directly instead of separate boundaryLevel state
  const [showDataValidation, setShowDataValidation] = useState(false);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUser();
  const { isDarkMode } = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const [selectedCounty, setSelectedCounty] = useState(null);
  
  // Color scheme dictionary mapping dropdown values to gradients
  const COLOR_SCHEMES = {
    'orange-red': ['#ffedd5', '#fed7aa', '#fde047', '#fbbf24', '#fb923c', '#f87171', '#dc2626'], // Orange to Red (reversed)
    'green-blue': ['#22c55e', '#16a34a', '#047857', '#059669', '#3b82f6', '#93c5fd', '#dbeafe'], // Green to Blue (reversed)
    'yellow-purple': ['#fef3c7', '#fde047', '#eab308', '#dc2626', '#b91c1c', '#7c2d12', '#581c87'], // Yellow to Purple (reversed)
    'green-red': ['#22c55e', '#86efac', '#fde047', '#fbbf24', '#fb923c', '#f87171', '#dc2626'], // Green to Red
    'grayscale': ['#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151'] // Light to Dark Gray
  };

  // Calculate scaling breakpoints based on method
  const calculateBreakpoints = (values, method = 'linear') => {
    const sortedValues = values.filter(v => v > 0).sort((a, b) => a - b);
    if (sortedValues.length === 0) return [1, 10, 20, 30, 40, 50, 60, 70];
    
    const min = sortedValues[0];
    const max = sortedValues[sortedValues.length - 1];
    
    switch (method) {
      case 'quantile': {
        // Equal distribution - each color gets same number of features
        const breakpoints = [];
        for (let i = 0; i <= 6; i++) {
          const index = Math.floor((sortedValues.length - 1) * (i / 6));
          breakpoints.push(sortedValues[index]);
        }
        return breakpoints;
      }
      
      case 'logarithmic': {
        // Logarithmic scale for data with extreme outliers
        const logMin = Math.log10(min || 1);
        const logMax = Math.log10(max);
        const breakpoints = [];
        for (let i = 0; i <= 6; i++) {
          const logValue = logMin + (logMax - logMin) * (i / 6);
          breakpoints.push(Math.pow(10, logValue));
        }
        return breakpoints;
      }
      
      case 'percentile': {
        // Cap at 95th percentile to reduce impact of extreme outliers
        const p95Index = Math.floor(sortedValues.length * 0.95);
        const cappedMax = sortedValues[p95Index];
        const breakpoints = [];
        for (let i = 0; i <= 6; i++) {
          breakpoints.push(min + (cappedMax - min) * (i / 6));
        }
        return breakpoints;
      }
      
      case 'jenks': {
        // Natural breaks (simplified version)
        // This is a simplified Jenks algorithm - just uses standard deviations
        const mean = sortedValues.reduce((a, b) => a + b, 0) / sortedValues.length;
        const stdDev = Math.sqrt(sortedValues.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / sortedValues.length);
        return [
          min,
          Math.max(min, mean - 2 * stdDev),
          Math.max(min, mean - stdDev),
          mean,
          Math.min(max, mean + stdDev),
          Math.min(max, mean + 2 * stdDev),
          max
        ];
      }
      
      case 'linear':
      default: {
        // Linear scale - equal intervals
        const breakpoints = [];
        for (let i = 0; i <= 6; i++) {
          breakpoints.push(min + (max - min) * (i / 6));
        }
        return breakpoints;
      }
    }
  };

  // Generate color gradients based on selected color style and scale
  const getColorGradient = (style, values) => {
    const colors = COLOR_SCHEMES[style] || COLOR_SCHEMES['orange-red'];
    const breakpoints = calculateBreakpoints(values, colorScale);
    
    // Ensure valid breakpoints
    const safeBreakpoints = breakpoints.map((val, i) => {
      if (i === 0) return Math.max(0.01, val);
      return Math.max(breakpoints[i-1] + 0.01, val);
    });
    
    const steps = [
      0, '#f0f0f0', // No data color (light gray)
    ];
    
    safeBreakpoints.forEach((breakpoint, i) => {
      steps.push(breakpoint, colors[i]);
    });
    
    return [
      'interpolate',
      ['linear'],
      ['get', colorMetric],
      ...steps
    ];
  };
  
  // Watch for dark mode changes and update map style
  useEffect(() => {
    if (mapInstanceRef.current && mapInstanceRef.current.isStyleLoaded()) {
      const newStyle = isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12';
      mapInstanceRef.current.setStyle(newStyle);
    }
  }, [isDarkMode]);
  
  // Watch for colorMetric, colorStyle, or colorScale changes and update map layer paint properties
  useEffect(() => {
    // Only update if map is loaded and we have data
    if (!mapInstanceRef.current || !mapInstanceRef.current.isStyleLoaded() || !geoJsonData) {
      return;
    }
    
    const map = mapInstanceRef.current;
    
    // Only update the relevant layer based on view type
    if (viewType === 'state' && map.getLayer('states-fill')) {
      // Recalculate values for the new metric
      const metricValues = geoJsonData.features.map(f => f.properties[colorMetric] || 0);
      const validValues = metricValues.filter(v => v > 0);
      
      if (validValues.length === 0) {
        console.warn(`⚠️ No valid data found for metric: ${colorMetric}. Using fallback to load_count.`);
        // Fallback to load_count if the selected metric has no data
        const fallbackValues = geoJsonData.features.map(f => f.properties['load_count'] || 0);
        map.setPaintProperty('states-fill', 'fill-color', getColorGradient(colorStyle, fallbackValues));
      } else {
        const maxValue = Math.max(...metricValues);
        const minValue = Math.min(...validValues);
        console.log(`📊 State ${colorMetric} range: ${minValue.toFixed(2)} to ${maxValue.toFixed(2)}`);
        // Update the paint property with selected color style
        map.setPaintProperty('states-fill', 'fill-color', getColorGradient(colorStyle, metricValues));
        console.log(`🎨 Updated state coloring to ${colorMetric} with ${colorScale} scale`);
      }
    } else if (viewType === 'county' && map.getLayer('counties-fill')) {
      // For counties, need to get the merged features from the source
      const source = map.getSource('counties-source');
      if (source && source._data && source._data.features) {
        const metricValues = source._data.features.map(f => f.properties[colorMetric] || 0);
        const validValues = metricValues.filter(v => v > 0);
        
        if (validValues.length === 0) {
          console.warn(`⚠️ No valid data found for metric: ${colorMetric}. Using fallback to load_count.`);
          // Fallback to load_count if the selected metric has no data
          const fallbackValues = source._data.features.map(f => f.properties['load_count'] || 0);
          map.setPaintProperty('counties-fill', 'fill-color', getColorGradient(colorStyle, fallbackValues));
        } else {
          const maxValue = Math.max(...metricValues);
          const minValue = Math.min(...validValues);
          console.log(`📊 County ${colorMetric} range: ${minValue.toFixed(2)} to ${maxValue.toFixed(2)}`);
          // Update the paint property with selected color style
          map.setPaintProperty('counties-fill', 'fill-color', getColorGradient(colorStyle, metricValues));
          console.log(`🎨 Updated county coloring to ${colorMetric} with ${colorScale} scale`);
        }
      }
    }
  }, [colorMetric, colorStyle, colorScale, viewType]); // Removed mapInstanceRef.current and geoJsonData to prevent unnecessary re-runs

  // Function to fetch aggregated data from backend
  const fetchAggregatedData = async (viewType, filters) => {
    setIsLoading(true);
    try {
      console.log(`Fetching aggregated ${viewType} data from backend API`);

      // Use the backend API to fetch heat map data
      let data;
      if (viewType === 'state') {
        console.log('[MapResults] Calling backend heat map states API...');
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const apiUrl = isLocal ? 'http://64.225.20.106:8008/api/heat-map-states' : '/backend/heat-map-states';
        console.log('[MapResults] API URL:', apiUrl);
        try {
          // Get fresh Auth0 token
          const token = await getAccessTokenSilently();
          console.log('[MapResults] Got Auth0 token for states heat map API');
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              equipment_types: filters.equipment_types || undefined,
              pickup_date_begin: filters.pickup_date_begin,
              pickup_date_end: filters.pickup_date_end
            })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          data = await response.json();
          console.log('[MapResults] Heat map states response:', data);
        } catch (error) {
          console.error('[MapResults] Heat map states API failed:', error);
          data = { success: false, error: error.message };
        }
      } else if (viewType === 'county') {
        console.log('[MapResults] Calling backend heat map counties API...');
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const apiUrl = isLocal ? 'http://64.225.20.106:8008/api/heat-map-counties' : '/backend/heat-map-counties';
        console.log('[MapResults] API URL:', apiUrl);
        try {
          // Get fresh Auth0 token
          const token = await getAccessTokenSilently();
          console.log('[MapResults] Got Auth0 token for counties heat map API');
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              equipment_types: filters.equipment_types || undefined,
              pickup_date_begin: filters.pickup_date_begin,
              pickup_date_end: filters.pickup_date_end
            })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          data = await response.json();
          console.log('[MapResults] Heat map counties response:', data);
        } catch (error) {
          console.error('[MapResults] Heat map counties API failed:', error);
          data = { success: false, error: error.message };
        }
      } else {
        // For other view types, return early
        console.log(`View type ${viewType} does not use backend aggregation`);
        setIsLoading(false);
        return;
      }

      console.log(`Received aggregated ${viewType} data:`, data);
      
      // Log the actual fields available in the first feature to verify avg_payment and avg_rate
      if (data && data.features && data.features.length > 0) {
        const sampleFeature = data.features[0];
        console.log(`📊 Available fields in ${viewType} data:`, Object.keys(sampleFeature.properties));
        console.log(`📊 Sample ${viewType} properties:`, sampleFeature.properties);
        
        // Check specifically for avg_payment and avg_rate
        const hasAvgPayment = 'avg_payment' in sampleFeature.properties;
        const hasAvgRate = 'avg_rate' in sampleFeature.properties;
        
        console.log(`✅ Backend provides avg_payment: ${hasAvgPayment} (value: ${sampleFeature.properties.avg_payment})`);
        console.log(`✅ Backend provides avg_rate: ${hasAvgRate} (value: ${sampleFeature.properties.avg_rate})`);
        
        // Log all features to see if any have the fields
        const featuresWithAvgPayment = data.features.filter(f => f.properties.avg_payment > 0).length;
        const featuresWithAvgRate = data.features.filter(f => f.properties.avg_rate > 0).length;
        console.log(`📊 Features with avg_payment > 0: ${featuresWithAvgPayment}/${data.features.length}`);
        console.log(`📊 Features with avg_rate > 0: ${featuresWithAvgRate}/${data.features.length}`);
      }
      
      setGeoJsonData(data);
      
      // Convert features to aggregated data format for validation section
      const aggregated = {};
      let totalLoads = 0;
      
      if (data.features && Array.isArray(data.features)) {
        data.features.forEach((feature, index) => {
        const props = feature.properties;
        const key = props.state || props.city_state || `item-${index}`;
        const loadCount = props.load_count || 0;
        
        totalLoads += loadCount;
        
        aggregated[key] = {
          id: key,
          name: props.state_name || props.city_state || key,
          load_count: loadCount,
          avg_payment: props.avg_payment || 0,
          avg_rate: props.avg_rate || 0,
          avg_mileage: props.avg_mileage || 0,
          total_payment: props.total_payment || 0,
        };
      });
      }
      
      // Add total loads count to aggregated data
      aggregated._metadata = {
        totalLoads: totalLoads,
        totalLocations: data.features ? data.features.length : 0
      };
      
      setAggregatedData(aggregated);
      return data;
      
    } catch (error) {
      console.error(`❌ Error fetching aggregated ${viewType} data:`, error);
      
      // Don't load mock data - show the actual error instead
      setIsLoading(false);
      console.error(`🚫 Heat Map API failed - Error: ${error.message}`);
      
      // Set error state instead of mock data
      setGeoJsonData(null);
      setAggregatedData({});
      
      // Show user-friendly error message
      alert(`Heat Map data loading failed: ${error.message}\n\nPlease check:\n1. Backend API is running\n2. Authentication token is valid\n3. Network connectivity\n\nCheck browser console for detailed error logs.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get state code from state name
  const getStateCodeFromName = (stateName) => {
    const stateMap = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
      'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    };
    return stateMap[stateName] || stateName;
  };

  // Fetch aggregated data when component mounts or parameters change
  useEffect(() => {
    if (isVisible && (viewType === 'state' || viewType === 'county')) {
      console.log(`🔄 Fetching aggregated data for ${viewType} view`);
      fetchAggregatedData(viewType, filters);
    }
  }, [isVisible, viewType, filters]);

  // Function to get coordinates from city name for map centering
  const getCityCoordinates = (cityState) => {
    // Common US cities coordinates for quick lookup
    const cityCoords = {
      'Chicago, IL': [-87.6298, 41.8781],
      'Los Angeles, CA': [-118.2437, 34.0522],
      'New York, NY': [-74.0059, 40.7128],
      'Houston, TX': [-95.3698, 29.7604],
      'Phoenix, AZ': [-112.0740, 33.4484],
      'Philadelphia, PA': [-75.1652, 39.9526],
      'San Antonio, TX': [-98.4936, 29.4241],
      'San Diego, CA': [-117.1611, 32.7157],
      'Dallas, TX': [-96.7970, 32.7767],
      'San Jose, CA': [-121.8863, 37.3382],
      'Austin, TX': [-97.7431, 30.2672],
      'Jacksonville, FL': [-81.6557, 30.3322],
      'Fort Worth, TX': [-97.3301, 32.7555],
      'Columbus, OH': [-82.9988, 39.9612],
      'Indianapolis, IN': [-86.1581, 39.7684],
      'Charlotte, NC': [-80.8431, 35.2271],
      'San Francisco, CA': [-122.4194, 37.7749],
      'Seattle, WA': [-122.3321, 47.6062],
      'Denver, CO': [-104.9903, 39.7392],
      'Washington, DC': [-77.0369, 38.9072],
      'Boston, MA': [-71.0589, 42.3601],
      'El Paso, TX': [-106.4850, 31.7619],
      'Detroit, MI': [-83.0458, 42.3314],
      'Nashville, TN': [-86.7816, 36.1627],
      'Portland, OR': [-122.6784, 45.5152],
      'Memphis, TN': [-90.0490, 35.1495],
      'Oklahoma City, OK': [-97.5164, 35.4676],
      'Las Vegas, NV': [-115.1398, 36.1699],
      'Louisville, KY': [-85.7585, 38.2527],
      'Baltimore, MD': [-76.6122, 39.2904],
      'Milwaukee, WI': [-87.9065, 43.0389],
      'Atlanta, GA': [-84.3880, 33.7490],
      'Miami, FL': [-80.1918, 25.7617],
      'Kansas City, MO': [-94.5786, 39.0997],
      'Tampa, FL': [-82.4572, 27.9506],
      'Cleveland, OH': [-81.6944, 41.4993],
      'New Orleans, LA': [-90.0715, 29.9511],
      'Minneapolis, MN': [-93.2650, 44.9778]
    };

    // Return coordinates if found, otherwise default to center of US
    return cityCoords[cityState] || [-95.7129, 37.0902];
  };

  // Convert FIPS state code to state name
  const getStateName = (stateCode) => {
    const stateNames = {
      '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas', '06': 'California',
      '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware', '11': 'District of Columbia',
      '12': 'Florida', '13': 'Georgia', '15': 'Hawaii', '16': 'Idaho', '17': 'Illinois',
      '18': 'Indiana', '19': 'Iowa', '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana',
      '23': 'Maine', '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota',
      '28': 'Mississippi', '29': 'Missouri', '30': 'Montana', '31': 'Nebraska', '32': 'Nevada',
      '33': 'New Hampshire', '34': 'New Jersey', '35': 'New Mexico', '36': 'New York', '37': 'North Carolina',
      '38': 'North Dakota', '39': 'Ohio', '40': 'Oklahoma', '41': 'Oregon', '42': 'Pennsylvania',
      '44': 'Rhode Island', '45': 'South Carolina', '46': 'South Dakota', '47': 'Tennessee',
      '48': 'Texas', '49': 'Utah', '50': 'Vermont', '51': 'Virginia', '53': 'Washington',
      '54': 'West Virginia', '55': 'Wisconsin', '56': 'Wyoming'
    };
    return stateNames[stateCode] || `State ${stateCode}`;
  };


  // Map state codes to US Census regions
  const getStateRegion = (stateCode) => {
    const regionMap = {
      // Northeast
      'CT': 'Northeast', 'ME': 'Northeast', 'MA': 'Northeast', 'NH': 'Northeast',
      'RI': 'Northeast', 'VT': 'Northeast', 'NJ': 'Northeast', 'NY': 'Northeast',
      'PA': 'Northeast',
      
      // Midwest
      'IL': 'Midwest', 'IN': 'Midwest', 'MI': 'Midwest', 'OH': 'Midwest', 'WI': 'Midwest',
      'IA': 'Midwest', 'KS': 'Midwest', 'MN': 'Midwest', 'MO': 'Midwest', 'NE': 'Midwest',
      'ND': 'Midwest', 'SD': 'Midwest',
      
      // South
      'DE': 'South', 'FL': 'South', 'GA': 'South', 'MD': 'South', 'NC': 'South',
      'SC': 'South', 'VA': 'South', 'DC': 'South', 'WV': 'South',
      'AL': 'South', 'KY': 'South', 'MS': 'South', 'TN': 'South', 'AR': 'South',
      'LA': 'South', 'OK': 'South', 'TX': 'South',
      
      // West
      'AZ': 'West', 'CO': 'West', 'ID': 'West', 'MT': 'West', 'NV': 'West',
      'NM': 'West', 'UT': 'West', 'WY': 'West', 'AK': 'West', 'CA': 'West',
      'HI': 'West', 'OR': 'West', 'WA': 'West'
    };
    return regionMap[stateCode] || 'Unknown';
  };

  // Enhanced aggregation by state for choropleth mapping
  const aggregateDataByState = (results, metric = 'volume') => {
    const stateData = {};
    
    results.forEach(load => {
      const originState = load.OriginState;
      const destState = load.DestinationState;
      const rate = parseFloat(load.RatePerMile) || 0;
      const payment = parseFloat(load.PaymentAmount) || 0;
      const mileage = parseFloat(load.Mileage) || 0;
      const totalRevenue = payment;
      
      // Initialize state data structure
      const initStateData = (state) => ({
        state: state,
        // Volume metrics
        totalLoads: 0,
        originLoads: 0,
        destinationLoads: 0,
        // Financial metrics  
        totalRevenue: 0,
        totalPayment: 0,
        totalRate: 0,
        avgRate: 0,
        avgPayment: 0,
        avgRevenue: 0,
        // Geographic metrics
        totalMileage: 0,
        avgMileage: 0,
        freightDensity: 0, // loads per mile
        // Economic impact
        economicImpact: 0, // total revenue generated
        revenuePerMile: 0
      });
      
      // Process origin state
      if (originState && originState !== 'XX') {
        if (!stateData[originState]) {
          stateData[originState] = initStateData(originState);
        }
        stateData[originState].totalLoads++;
        stateData[originState].originLoads++;
        stateData[originState].totalRevenue += totalRevenue;
        stateData[originState].totalPayment += payment;
        stateData[originState].totalRate += rate;
        stateData[originState].totalMileage += mileage;
        stateData[originState].economicImpact += totalRevenue;
      }
      
      // Process destination state  
      if (destState && destState !== 'XX' && destState !== originState) {
        if (!stateData[destState]) {
          stateData[destState] = initStateData(destState);
        }
        stateData[destState].totalLoads++;
        stateData[destState].destinationLoads++;
        stateData[destState].totalRevenue += totalRevenue;
        stateData[destState].totalPayment += payment;
        stateData[destState].totalRate += rate;
        stateData[destState].totalMileage += mileage;
        stateData[destState].economicImpact += totalRevenue;
      }
    });
    
    // Calculate derived metrics for choropleth mapping
    Object.values(stateData).forEach(state => {
      if (state.totalLoads > 0) {
        state.avgRate = state.totalRate / state.totalLoads;
        state.avgPayment = state.totalPayment / state.totalLoads;
        state.avgRevenue = state.totalRevenue / state.totalLoads;
        state.avgMileage = state.totalMileage / state.totalLoads;
        state.freightDensity = state.totalLoads / Math.max(state.totalMileage, 1);
        state.revenuePerMile = state.totalRevenue / Math.max(state.totalMileage, 1);
      }
    });
    
    console.log('📊 Enhanced state aggregation complete:', Object.keys(stateData).length, 'states with choropleth metrics');
    return stateData;
  };

  // Aggregate data by county using ofips and dfips columns
  const aggregateDataByCounty = (results) => {
    const countyData = {};
    
    // Debug: Check what FIPS columns are available
    if (results.length > 0) {
      const firstLoad = results[0];
      console.log('🔍 Available FIPS columns in load data:', {
        ofips: firstLoad.ofips,
        dfips: firstLoad.dfips,
        OriginCountyFips: firstLoad.OriginCountyFips,
        DestinationCountyFips: firstLoad.DestinationCountyFips,
        oCountyFips: firstLoad.oCountyFips,
        dCountyFips: firstLoad.dCountyFips,
        availableKeys: Object.keys(firstLoad).filter(key => key.toLowerCase().includes('fips'))
      });
    }
    
    results.forEach(load => {
      // Use ofips and dfips columns from parquet data
      const originCountyFips = load.ofips || load.OriginCountyFips || load.oCountyFips;
      const destCountyFips = load.dfips || load.DestinationCountyFips || load.dCountyFips;
      const rate = parseFloat(load.RatePerMile) || 0;
      const payment = parseFloat(load.PaymentAmount) || 0;
      const mileage = parseFloat(load.Mileage) || 0;
      
      // Initialize county data structure with enhanced metrics
      const initCountyData = (fips) => ({
        fips: fips,
        totalLoads: 0,
        originLoads: 0,
        destinationLoads: 0,
        totalPayment: 0,
        totalRevenue: 0,
        totalRate: 0,
        totalMileage: 0,
        avgRate: 0,
        avgPayment: 0,
        avgRevenue: 0,
        avgMileage: 0,
        freightDensity: 0,
        economicImpact: 0
      });
      
      // Aggregate origin county data
      if (originCountyFips && originCountyFips !== '' && originCountyFips !== '0') {
        if (!countyData[originCountyFips]) {
          countyData[originCountyFips] = initCountyData(originCountyFips);
        }
        countyData[originCountyFips].totalLoads++;
        countyData[originCountyFips].originLoads++;
        countyData[originCountyFips].totalPayment += payment;
        countyData[originCountyFips].totalRevenue += payment;
        countyData[originCountyFips].totalRate += rate;
        countyData[originCountyFips].totalMileage += mileage;
        countyData[originCountyFips].economicImpact += payment;
      }
      
      // Aggregate destination county data
      if (destCountyFips && destCountyFips !== '' && destCountyFips !== '0' && destCountyFips !== originCountyFips) {
        if (!countyData[destCountyFips]) {
          countyData[destCountyFips] = initCountyData(destCountyFips);
        }
        countyData[destCountyFips].totalLoads++;
        countyData[destCountyFips].destinationLoads++;
        countyData[destCountyFips].totalPayment += payment;
        countyData[destCountyFips].totalRevenue += payment;
        countyData[destCountyFips].totalRate += rate;
        countyData[destCountyFips].totalMileage += mileage;
        countyData[destCountyFips].economicImpact += payment;
      }
    });
    
    // Calculate derived metrics for choropleth mapping
    Object.values(countyData).forEach(county => {
      if (county.totalLoads > 0) {
        county.avgRate = county.totalRate / county.totalLoads;
        county.avgPayment = county.totalPayment / county.totalLoads;
        county.avgRevenue = county.totalRevenue / county.totalLoads;
        county.avgMileage = county.totalMileage / county.totalLoads;
        county.freightDensity = county.totalLoads / Math.max(county.totalMileage, 1);
      }
    });
    
    console.log('📊 Enhanced county aggregation complete:', Object.keys(countyData).length, 'counties with FIPS data');
    return countyData;
  };

  // Aggregate data by region
  const aggregateDataByRegion = (results) => {
    const regionData = {};
    
    results.forEach(load => {
      // Get region from origin and destination states
      const originRegion = getStateRegion(load.OriginState);
      const destRegion = getStateRegion(load.DestinationState);
      const rate = parseFloat(load.RatePerMile) || 0;
      const payment = parseFloat(load.PaymentAmount) || 0;
      
      // Aggregate origin region data
      if (originRegion && originRegion !== 'Unknown') {
        if (!regionData[originRegion]) {
          regionData[originRegion] = {
            region: originRegion,
            totalLoads: 0,
            totalPayment: 0,
            totalRate: 0,
            avgRate: 0,
            avgPayment: 0
          };
        }
        regionData[originRegion].totalLoads++;
        regionData[originRegion].totalPayment += payment;
        regionData[originRegion].totalRate += rate;
      }
      
      // Aggregate destination region data
      if (destRegion && destRegion !== 'Unknown' && destRegion !== originRegion) {
        if (!regionData[destRegion]) {
          regionData[destRegion] = {
            region: destRegion,
            totalLoads: 0,
            totalPayment: 0,
            totalRate: 0,
            avgRate: 0,
            avgPayment: 0
          };
        }
        regionData[destRegion].totalLoads++;
        regionData[destRegion].totalPayment += payment;
        regionData[destRegion].totalRate += rate;
      }
    });
    
    // Calculate averages
    Object.values(regionData).forEach(region => {
      if (region.totalLoads > 0) {
        region.avgRate = region.totalRate / region.totalLoads;
        region.avgPayment = region.totalPayment / region.totalLoads;
      }
    });
    
    console.log('📊 Region aggregation complete:', Object.keys(regionData).length, 'regions');
    return regionData;
  };

  // Add simple state outlines using GeoJSON
  const addStateData = (map, geoJsonData) => {
    console.log('🗺️ Adding state data using backend GeoJSON with aggregated data');
    
    // Clear existing state layers and sources
    try {
      if (map.getLayer('states-fill')) {
        map.removeLayer('states-fill');
      }
      if (map.getLayer('states-outline')) {
        map.removeLayer('states-outline');
      }
    } catch (e) {
      console.warn('Failed to remove existing state layers:', e);
    }
    
    try {
      if (map.getSource('states-source')) {
        map.removeSource('states-source');
      }
    } catch (e) {
      console.warn('Failed to remove states-source:', e);
    }

    // Clear county layers when switching to state view
    try {
      if (map.getLayer('counties-fill')) {
        map.removeLayer('counties-fill');
      }
      if (map.getLayer('counties-outline')) {
        map.removeLayer('counties-outline');
      }
    } catch (e) {
      console.warn('Failed to remove county layers:', e);
    }
    
    try {
      if (map.getSource('counties-source')) {
        map.removeSource('counties-source');
      }
    } catch (e) {
      console.warn('Failed to remove counties-source:', e);
    }
    
    if (!geoJsonData || !geoJsonData.features) {
      console.warn('No GeoJSON data available for states');
      return;
    }

    // Add the aggregated GeoJSON data as a source
    map.addSource('states-source', {
      type: 'geojson',
      data: geoJsonData
    });

    // Find the maximum value for the selected metric for color scaling
    const metricValues = geoJsonData.features.map(f => f.properties[colorMetric] || 0);
    const validValues = metricValues.filter(v => v > 0);
    
    let maxValue, minValue;
    if (validValues.length === 0) {
      console.warn(`⚠️ No valid data for ${colorMetric}, using load_count as fallback`);
      const fallbackValues = geoJsonData.features.map(f => f.properties['load_count'] || 0);
      maxValue = Math.max(...fallbackValues) || 100;
      minValue = Math.min(...fallbackValues.filter(v => v > 0)) || 1;
    } else {
      maxValue = Math.max(...metricValues);
      minValue = Math.min(...validValues);
      console.log(`📊 State data range for ${colorMetric}: ${minValue.toFixed(2)} to ${maxValue.toFixed(2)}`);
    }

    // Add fill layer with choropleth coloring based on selected metric
    map.addLayer({
      'id': 'states-fill',
      'type': 'fill',
      'source': 'states-source',
      'paint': {
        'fill-color': getColorGradient(colorStyle, metricValues),
        'fill-opacity': 0.7
      }
    });

    // Add outline layer
    map.addLayer({
      'id': 'states-outline',
      'type': 'line',
      'source': 'states-source',
      'paint': {
        'line-color': '#2d3748',
        'line-width': 1.5
      }
    });

    // Add click events for state details
    map.on('click', 'states-fill', (e) => {
      const props = e.features[0].properties;
      
      // Set selected state for metrics display
      setSelectedCounty({
        name: props.state || 'Unknown State',
        state: '',  // States don't have a parent state
        fips: props.state_code || '',
        loadCount: props.load_count || 0,
        totalPayment: props.total_payment || 0,
        avgPayment: props.avg_payment || 0,
        avgMileage: props.avg_mileage || 0,
        avgRate: props.avg_rate || 0
      });
    });

    // Change cursor on hover
    map.on('mouseenter', 'states-fill', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'states-fill', () => {
      map.getCanvas().style.cursor = '';
    });
    
    console.log(`✅ Added ${geoJsonData.features.length} states to map with choropleth coloring`);
  };

  // Add region boundaries using aggregated states
  const addRegionData = (map, validResults) => {
    console.log('🗺️ Adding region boundaries using state aggregation');
    
    // Clear existing region layers
    try {
      if (map.getLayer('regions-fill')) {
        map.removeLayer('regions-fill');
      }
      if (map.getLayer('regions-outline')) {
        map.removeLayer('regions-outline');
      }
    } catch (e) {
      console.warn('Failed to remove region layers:', e);
    }
    
    try {
      if (map.getSource('regions-source')) {
        map.removeSource('regions-source');
      }
    } catch (e) {
      console.warn('Failed to remove regions-source:', e);
    }

    // Clear state and county layers when switching to region view
    try {
      if (map.getLayer('states-outline')) {
        map.removeLayer('states-outline');
      }
      if (map.getLayer('counties-fill')) {
        map.removeLayer('counties-fill');
      }
      if (map.getLayer('counties-outline')) {
        map.removeLayer('counties-outline');
      }
    } catch (e) {
      console.warn('Failed to remove other layers:', e);
    }
    
    try {
      if (map.getSource('states-source')) {
        map.removeSource('states-source');
      }
      if (map.getSource('counties-source')) {
        map.removeSource('counties-source');
      }
    } catch (e) {
      console.warn('Failed to remove other sources:', e);
    }
    
    // Load states GeoJSON to create regions
    fetch('/geojson-states.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('States GeoJSON not found');
        }
        return response.json();
      })
      .then(statesGeoJSON => {
        console.log('📦 Loaded states GeoJSON for regions:', statesGeoJSON.features.length, 'features');
        
        // Aggregate load data by region
        const regionLoadData = aggregateDataByRegion(validResults);
        
        // Group states by region - keep it simple
        const regionFeatures = {};
        
        statesGeoJSON.features.forEach(feature => {
          const stateName = feature.properties.NAME;
          const stateCode = getStateCodeFromName(stateName);
          const region = getStateRegion(stateCode);
          
          if (region !== 'Unknown') {
            if (!regionFeatures[region]) {
              regionFeatures[region] = {
                type: "Feature",
                properties: {
                  REGION: region,
                  NAME: region,
                  loadCount: 0,
                  avgRate: 0,
                  avgPayment: 0
                },
                geometry: {
                  type: "MultiPolygon",
                  coordinates: []
                }
              };
            }
            
            // Add state polygon to region
            if (feature.geometry.type === "Polygon") {
              regionFeatures[region].geometry.coordinates.push(feature.geometry.coordinates);
            } else if (feature.geometry.type === "MultiPolygon") {
              feature.geometry.coordinates.forEach(polygon => {
                regionFeatures[region].geometry.coordinates.push(polygon);
              });
            }
          }
        });
        
        // Add load data to region features
        Object.keys(regionFeatures).forEach(regionName => {
          const loadData = regionLoadData[regionName];
          if (loadData) {
            regionFeatures[regionName].properties.loadCount = loadData.totalLoads;
            regionFeatures[regionName].properties.avgRate = loadData.avgRate;
            regionFeatures[regionName].properties.avgPayment = loadData.avgPayment;
          }
        });
        
        const regionsGeoJSON = {
          type: "FeatureCollection",
          features: Object.values(regionFeatures)
        };
        
        console.log('📦 Created regions from states:', regionsGeoJSON.features.length, 'regions');
        
        // Add source
        if (!map.getSource('regions-source')) {
          map.addSource('regions-source', {
            type: 'geojson',
            data: regionsGeoJSON
          });
        } else {
          map.getSource('regions-source').setData(regionsGeoJSON);
        }
        
        // Add fill layer for region heat map - no internal boundaries shown
        if (!map.getLayer('regions-fill')) {
          map.addLayer({
            id: 'regions-fill',
            type: 'fill',
            source: 'regions-source',
            paint: {
              'fill-color': [
                'case',
                ['==', ['get', 'loadCount'], 0], '#f0f0f0',  // Light gray for no loads
                [
                  'interpolate',
                  ['linear'],
                  ['get', 'loadCount'],
                  1, '#fee5d9',       // Light orange for few loads
                  100, '#fcae91',     // Medium orange
                  500, '#fb6a4a',     // Orange-red
                  1000, '#de2d26',    // Red
                  2000, '#a50f15'     // Dark red for many loads
                ]
              ],
              'fill-opacity': 0.8,
              'fill-outline-color': 'rgba(0,0,0,0)'  // Transparent outline to hide internal boundaries
            }
          });
        }

        // Add only external outline layer with data-driven styling
        if (!map.getLayer('regions-outline')) {
          map.addLayer({
            id: 'regions-outline',
            type: 'line',
            source: 'regions-source',
            paint: {
              'line-color': '#333333',     // Dark gray for region boundaries
              'line-width': [
                'case',
                ['>', ['get', 'loadCount'], 0], 2,   // Thicker lines for regions with data
                1                                     // Thinner lines for regions without data
              ],
              'line-opacity': 0.4,         // Lower opacity to be subtle
              'line-blur': 1               // Blur to merge internal boundaries visually
            }
          });
        }
        
        // Add click handler
        map.on('click', 'regions-fill', (e) => {
          const props = e.features[0].properties;
          console.log('🖱️ Clicked region:', props);
          
          const regionName = props.NAME || 'Unknown Region';
          const loadCount = props.loadCount || 0;
          const avgRate = props.avgRate || 0;
          const avgPayment = props.avgPayment || 0;
          
          const popup = new window.mapboxgl.Popup({ offset: 25 })
            .setLngLat(e.lngLat)
            .setHTML(`
              <div class="p-3">
                <div class="font-semibold text-lg mb-2">${regionName} Region</div>
                <div class="text-sm space-y-1">
                  <div><strong>Total Loads:</strong> ${loadCount.toLocaleString()}</div>
                  ${loadCount > 0 ? `
                    <div><strong>Avg Rate/Mile:</strong> $${avgRate.toFixed(2)}</div>
                    <div><strong>Avg Payment:</strong> $${avgPayment.toLocaleString()}</div>
                  ` : '<div class="text-gray-500">No freight activity</div>'}
                </div>
              </div>
            `);
          popup.addTo(map);
        });
        
        map.on('mouseenter', 'regions-fill', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        
        map.on('mouseleave', 'regions-fill', () => {
          map.getCanvas().style.cursor = '';
        });
        
        console.log('✅ Region boundaries and heat map added');
      })
      .catch(error => {
        console.error('❌ Failed to load states GeoJSON for regions:', error);
      });
  };

  // Add county boundaries using GeoJSON
  const addCountyData = (map, geoJsonData) => {
    console.log('🗺️ Loading county boundaries from geojson-counties-fips.json');
    
    // Clear existing layers and sources
    try {
      if (map.getLayer('counties-fill')) {
        map.removeLayer('counties-fill');
      }
      if (map.getLayer('counties-outline')) {
        map.removeLayer('counties-outline');
      }
      if (map.getLayer('cities-circles')) {
        map.removeLayer('cities-circles');
      }
    } catch (e) {
      console.warn('Failed to remove county/city layers:', e);
    }
    
    try {
      if (map.getSource('counties-source')) {
        map.removeSource('counties-source');
      }
      if (map.getSource('cities-source')) {
        map.removeSource('cities-source');
      }
    } catch (e) {
      console.warn('Failed to remove county/city sources:', e);
    }
    
    // Additional cleanup - force remove sources if they still exist
    try {
      if (map.getSource('counties-source')) {
        console.log('Force removing counties-source');
        map.removeSource('counties-source');
      }
    } catch (e) {
      console.warn('Force removal of counties-source failed:', e);
    }

    // Clear state layers when switching to county view
    try {
      if (map.getLayer('states-fill')) {
        map.removeLayer('states-fill');
      }
      if (map.getLayer('states-outline')) {
        map.removeLayer('states-outline');
      }
    } catch (e) {
      console.warn('Failed to remove states layer:', e);
    }
    
    try {
      if (map.getSource('states-source')) {
        map.removeSource('states-source');
      }
    } catch (e) {
      console.warn('Failed to remove states-source:', e);
    }
    
    // First, load the county boundaries from the local GeoJSON file
    fetch('/geojson-counties-fips.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('County GeoJSON not found');
        }
        return response.json();
      })
      .then(countiesGeoJSON => {
        console.log('📦 Loaded county GeoJSON:', countiesGeoJSON.features.length, 'counties');
        
        // If we have backend aggregated data, merge it with the boundaries
        let countyFeatures = countiesGeoJSON.features;
        if (geoJsonData && geoJsonData.features) {
          console.log('🔗 Merging backend aggregated data with county boundaries');
          
          // Create a map of FIPS codes to aggregated data
          const dataMap = {};
          geoJsonData.features.forEach(feature => {
            const fips = feature.properties.county_fips;
            if (fips) {
              dataMap[fips] = feature.properties;
            }
          });
          
          // Merge aggregated data with county boundaries
          countyFeatures = countiesGeoJSON.features.map(countyFeature => {
            // GeoJSON uses STATE + COUNTY to form FIPS code
            const fips = countyFeature.properties.STATE + countyFeature.properties.COUNTY;
            const aggregatedData = dataMap[fips];
            
            return {
              ...countyFeature,
              properties: {
                ...countyFeature.properties,
                ...(aggregatedData || {
                  load_count: 0,
                  avg_payment: 0,
                  avg_rate: 0,
                  density: 0
                })
              }
            };
          });
        } else {
          // No backend data, show empty counties
          console.log('📊 No backend data available, showing empty county boundaries');
          countyFeatures = countiesGeoJSON.features.map(feature => ({
            ...feature,
            properties: {
              ...feature.properties,
              load_count: 0,
              avg_payment: 0,
              avg_rate: 0,
              density: 0
            }
          }));
        }
        
        // Add county boundaries to map
        const countyGeoJSON = {
          type: 'FeatureCollection',
          features: countyFeatures
        };
        
        // Safely add counties source - remove first if it exists
        if (map.getSource('counties-source')) {
          map.removeSource('counties-source');
        }
        
        map.addSource('counties-source', {
          type: 'geojson',
          data: countyGeoJSON
        });
        
        // Find the maximum value for the selected metric for color scaling
        const metricValues = countyFeatures.map(f => f.properties[colorMetric] || 0);
        const validValues = metricValues.filter(v => v > 0);
        
        let maxValue, minValue;
        if (validValues.length === 0) {
          console.warn(`⚠️ No valid data for ${colorMetric}, using load_count as fallback`);
          const fallbackValues = countyFeatures.map(f => f.properties['load_count'] || 0);
          maxValue = Math.max(...fallbackValues) || 100;
          minValue = Math.min(...fallbackValues.filter(v => v > 0)) || 1;
        } else {
          maxValue = Math.max(...metricValues);
          minValue = Math.min(...validValues);
          console.log(`📊 County data range for ${colorMetric}: ${minValue.toFixed(2)} to ${maxValue.toFixed(2)}`);
        }
        
        // Add filled county layer with choropleth coloring
        map.addLayer({
          'id': 'counties-fill',
          'type': 'fill',
          'source': 'counties-source',
          'paint': {
            'fill-color': getColorGradient(colorStyle, metricValues),
            'fill-opacity': 0.7
          }
        });
        
        // Add county outline layer
        map.addLayer({
          'id': 'counties-outline',
          'type': 'line',
          'source': 'counties-source',
          'paint': {
            'line-color': '#ffffff',
            'line-width': 0.5,
            'line-opacity': 0.8
          }
        });
        
        // Add hover and click handlers for counties
        map.on('mouseenter', 'counties-fill', (e) => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'counties-fill', () => {
          map.getCanvas().style.cursor = '';
        });
        
        // Add click handler to show county metrics
        map.on('click', 'counties-fill', (e) => {
          if (e.features && e.features[0]) {
            const properties = e.features[0].properties;
            setSelectedCounty({
              name: properties.county_name || properties.NAME || 'Unknown County',
              state: properties.state || properties.STATE_NAME || '',
              fips: properties.county_fips,
              loadCount: properties.load_count || 0,
              totalPayment: properties.total_payment || 0,
              avgPayment: properties.avg_payment || 0,
              avgMileage: properties.avg_mileage || 0,
              avgRate: properties.avg_rate || 0
            });
          }
        });
        
        console.log(`✅ Added ${countyFeatures.length} counties to map with choropleth coloring`);
      })
      .catch(error => {
        console.error('❌ Failed to load county GeoJSON:', error);
        
        // Fallback: if we can't load county boundaries but have aggregated data, show as points
        if (geoJsonData && geoJsonData.features) {
          console.log('📍 Fallback: Showing aggregated data as points');
          addCityPointsFallback(map, geoJsonData);
        }
      });
  };
  
  // Fallback function to show aggregated data as points when county boundaries fail to load
  const addCityPointsFallback = (map, geoJsonData) => {

    // Add the aggregated city data as point features
    map.addSource('cities-source', {
      type: 'geojson',
      data: geoJsonData
    });

    // Find the maximum load count for sizing
    const maxLoads = Math.max(...geoJsonData.features.map(f => f.properties.load_count || 0));
    console.log(`📊 City data range: 0 to ${maxLoads} loads`);

    // Add circles for cities with load data
    map.addLayer({
      'id': 'cities-circles',
      'type': 'circle',
      'source': 'cities-source',
      'paint': {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['get', 'density'],
          0, 3,                        // Minimum radius
          Math.max(1, maxLoads * 0.2), 8,
          Math.max(1, maxLoads * 0.5), 15,
          Math.max(1, maxLoads * 0.8), 25,
          maxLoads, 35                 // Maximum radius
        ],
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'density'],
          0, '#f7fafc',      // Very light gray for no data
          1, '#90cdf4',      // Light blue
          Math.max(1, maxLoads * 0.2), '#63b3ed',   // Medium blue  
          Math.max(1, maxLoads * 0.4), '#3182ce',   // Darker blue
          Math.max(1, maxLoads * 0.6), '#2c5282',   // Dark blue
          Math.max(1, maxLoads * 0.8), '#1a365d',   // Darkest blue
          maxLoads, '#0f1a2e'                       // Very dark blue
        ],
        'circle-opacity': 0.8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#2d3748'
      }
    });

    // Add click events for city details
    map.on('click', 'cities-circles', (e) => {
      const props = e.features[0].properties;
      
      new window.mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(`
          <div class="p-3">
            <h3 class="font-bold text-lg">${props.city_state || props.city}</h3>
            <div class="mt-2 space-y-1 text-sm">
              <div><strong>Load Count:</strong> ${(props.load_count || 0).toLocaleString()}</div>
              <div><strong>Avg Payment:</strong> $${(props.avg_payment || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</div>
              <div><strong>Avg Rate:</strong> $${(props.avg_rate || 0).toFixed(2)}/mile</div>
              <div><strong>Avg Mileage:</strong> ${(props.avg_mileage || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})} miles</div>
            </div>
          </div>
        `)
        .addTo(map);
    });

    // Change cursor on hover
    map.on('mouseenter', 'cities-circles', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'cities-circles', () => {
      map.getCanvas().style.cursor = '';
    });
    
    console.log(`✅ Added ${geoJsonData.features.length} cities to map as circles`);
  };

  // Add data to map for city view
  const addCityData = (map, validResults) => {
    console.log('🎯 Adding city data with lat/lon coordinates');
    
    // Clear existing layers and sources properly
    ['origin-markers', 'destination-markers'].forEach(layerId => {
      try {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
      } catch (e) {
        console.warn(`Failed to remove layer ${layerId}:`, e);
      }
    });

    ['origin-points', 'destination-points'].forEach(sourceId => {
      try {
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      } catch (e) {
        console.warn(`Failed to remove source ${sourceId}:`, e);
      }
    });

    // Create origin points
    const originFeatures = validResults.map((load, index) => {
      const lat = parseFloat(load.oLat);
      const lon = parseFloat(load.oLon);
      
      if (!isNaN(lat) && !isNaN(lon)) {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          },
          properties: {
            id: load.ID || `origin-${index}`,
            city: load.OriginCity || 'Unknown',
            state: load.OriginState || 'XX',
            type: 'origin',
            equipment: load.Equipment || 'F',
            rate: load.RatePerMile || 0,
            payment: load.PaymentAmount || 0
          }
        };
      }
      return null;
    }).filter(Boolean);

    // Create destination points
    const destinationFeatures = validResults.map((load, index) => {
      const lat = parseFloat(load.dLat);
      const lon = parseFloat(load.dLon);
      
      if (!isNaN(lat) && !isNaN(lon)) {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          },
          properties: {
            id: load.ID || `destination-${index}`,
            city: load.DestinationCity || 'Unknown',
            state: load.DestinationState || 'XX',
            type: 'destination',
            equipment: load.Equipment || 'F',
            rate: load.RatePerMile || 0,
            payment: load.PaymentAmount || 0
          }
        };
      }
      return null;
    }).filter(Boolean);

    console.log(`📍 Found ${originFeatures.length} origin points and ${destinationFeatures.length} destination points`);

    // Add origin markers
    if (originFeatures.length > 0) {
      if (!map.getSource('origin-points')) {
        map.addSource('origin-points', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: originFeatures
          }
        });
      } else {
        map.getSource('origin-points').setData({
          type: 'FeatureCollection',
          features: originFeatures
        });
      }

      if (!map.getLayer('origin-markers')) {
        map.addLayer({
          id: 'origin-markers',
          type: 'circle',
          source: 'origin-points',
          paint: {
            'circle-radius': 6,
            'circle-color': '#3b82f6', // Blue for origins
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-opacity': 0.8
          }
        });
      }
    }

    // Add destination markers
    if (destinationFeatures.length > 0) {
      if (!map.getSource('destination-points')) {
        map.addSource('destination-points', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: destinationFeatures
          }
        });
      } else {
        map.getSource('destination-points').setData({
          type: 'FeatureCollection',
          features: destinationFeatures
        });
      }

      if (!map.getLayer('destination-markers')) {
        map.addLayer({
          id: 'destination-markers',
          type: 'circle',
          source: 'destination-points',
          paint: {
            'circle-radius': 6,
            'circle-color': '#22c55e', // Green for destinations
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-opacity': 0.8
          }
        });
      }
    }

    // Add popup handlers
    ['origin-markers', 'destination-markers'].forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.on('click', layerId, (e) => {
          const props = e.features[0].properties;
          const popup = new window.mapboxgl.Popup({ offset: 25 })
            .setLngLat(e.lngLat)
            .setHTML(`
              <div class="p-3 min-w-48">
                <div class="font-semibold text-lg mb-2">${props.type === 'origin' ? 'Origin' : 'Destination'}</div>
                <div class="space-y-1 text-sm">
                  <div><strong>City:</strong> ${props.city}, ${props.state}</div>
                  <div><strong>Equipment:</strong> ${props.equipment}</div>
                  <div><strong>Rate/Mile:</strong> $${parseFloat(props.rate).toFixed(2)}</div>
                  <div><strong>Payment:</strong> $${parseFloat(props.payment).toLocaleString()}</div>
                </div>
              </div>
            `);
          popup.addTo(map);
        });

        map.on('mouseenter', layerId, () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', layerId, () => {
          map.getCanvas().style.cursor = '';
        });
      }
    });
  };

  // Add route lines connecting origins to destinations
  const addRouteLines = (map, validResults) => {
    if (!showRoutes || viewType === 'state') return;
    
    console.log('🛣️ Adding route lines for', validResults.length, 'loads');
    
    // Clear existing route layers
    try {
      if (map.getLayer('route-lines')) {
        map.removeLayer('route-lines');
      }
    } catch (e) {
      console.warn('Failed to remove route-lines layer:', e);
    }
    
    try {
      if (map.getSource('routes')) {
        map.removeSource('routes');
      }
    } catch (e) {
      console.warn('Failed to remove routes source:', e);
    }
    
    // Create line features connecting origins to destinations
    const lineFeatures = validResults.map((load, index) => {
      const oLat = parseFloat(load.oLat);
      const oLon = parseFloat(load.oLon);
      const dLat = parseFloat(load.dLat);
      const dLon = parseFloat(load.dLon);
      
      if (!isNaN(oLat) && !isNaN(oLon) && !isNaN(dLat) && !isNaN(dLon)) {
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [oLon, oLat], // Origin coordinates
              [dLon, dLat]  // Destination coordinates
            ]
          },
          properties: {
            id: load.ID || `route-${index}`,
            origin: `${load.OriginCity}, ${load.OriginState}`,
            destination: `${load.DestinationCity}, ${load.DestinationState}`,
            equipment: load.Equipment || 'F',
            rate: load.RatePerMile || 0,
            payment: load.PaymentAmount || 0,
            mileage: load.Mileage || 0
          }
        };
      }
      return null;
    }).filter(Boolean);

    console.log(`📍 Created ${lineFeatures.length} route lines`);

    if (lineFeatures.length > 0) {
      // Add route lines source
      map.addSource('routes', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: lineFeatures
        }
      });

      // Add route lines layer
      map.addLayer({
        id: 'route-lines',
        type: 'line',
        source: 'routes',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#f59e0b', // Amber color for routes
          'line-width': 3,
          'line-opacity': 0.7
        }
      });

      // Add click handler for route lines
      map.on('click', 'route-lines', (e) => {
        const props = e.features[0].properties;
        const popup = new window.mapboxgl.Popup({ offset: 25 })
          .setLngLat(e.lngLat)
          .setHTML(`
            <div class="p-3 min-w-48">
              <div class="font-semibold text-lg mb-2">Freight Route</div>
              <div class="space-y-1 text-sm">
                <div><strong>Origin:</strong> ${props.origin}</div>
                <div><strong>Destination:</strong> ${props.destination}</div>
                <div><strong>Equipment:</strong> ${props.equipment}</div>
                <div><strong>Rate/Mile:</strong> $${parseFloat(props.rate).toFixed(2)}</div>
                <div><strong>Payment:</strong> $${parseFloat(props.payment).toLocaleString()}</div>
                <div><strong>Mileage:</strong> ${parseFloat(props.mileage).toLocaleString()} mi</div>
              </div>
            </div>
          `);
        popup.addTo(map);
      });

      map.on('mouseenter', 'route-lines', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'route-lines', () => {
        map.getCanvas().style.cursor = '';
      });
    }
  };

  // Map initialization
  useEffect(() => {
    if (!isVisible || mapInstanceRef.current) return;

    const initializeMap = () => {
      if (!window.mapboxgl) {
        console.log('Mapbox GL JS not loaded');
        return;
      }

      console.log('🗺️ Initializing map with city data');
      
      // Determine map center and zoom based on search origin
      let mapCenter = [-95.7129, 37.0902]; // Default center of USA
      let mapZoom = 4; // Default zoom
      
      if (searchOrigin && viewType === 'city') {
        mapCenter = getCityCoordinates(searchOrigin);
        mapZoom = 8; // Closer zoom for city searches
        console.log('🎯 Centering map on searched city:', searchOrigin, 'at coordinates:', mapCenter);
      }
      
      const map = new window.mapboxgl.Map({
        container: mapRef.current,
        style: isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12',
        center: mapCenter,
        zoom: mapZoom,
        accessToken: process.env.REACT_APP_MAPBOX_PUBLIC_KEY,
        pitch: 0, // Flat top-down view (0 degrees)
        bearing: 0, // Initial rotation
        antialias: true // Smooth rendering
      });

      map.on('load', () => {
        console.log('✅ Map loaded successfully');
        setMapLoaded(true);
        
        // Add data if available
        if (viewType === 'region' && results && results.length > 0) {
          addRegionData(map, results);
        } else if (viewType === 'state' && geoJsonData) {
          addStateData(map, geoJsonData);
        } else if (viewType === 'county' && geoJsonData) {
          addCountyData(map, geoJsonData);
        } else if (viewType === 'city' && results && results.length > 0) {
          addCityData(map, results);
          addRouteLines(map, results);
        }
      });

      map.on('error', (e) => {
        console.error('❌ Map error:', e);
        if (e.error) {
          console.error('Error details:', e.error.message || e.error);
        }
        if (e.layer) {
          console.error('Layer that caused error:', e.layer.id);
        }
      });

      // Add navigation controls (commented out to hide sidebar controls)
      // map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');

      mapInstanceRef.current = map;
    };

    // Load Mapbox if not already loaded
    if (!window.mapboxgl) {
      const script = document.createElement('script');
      script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
      script.onload = initializeMap;
      document.head.appendChild(script);
    } else {
      initializeMap();
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        setMapLoaded(false);
      }
    };
  }, [isVisible]);

  // Update map data when geoJsonData or viewType change
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current) {
      console.log('🔄 Updating map with new data');
      
      if (viewType === 'region' && results && results.length > 0) {
        addRegionData(mapInstanceRef.current, results);
        const aggregatedResult = aggregateDataByRegion(results);
        
        // Add metadata for total loads count
        const totalLoads = Object.values(aggregatedResult).reduce((sum, region) => sum + (region.totalLoads || 0), 0);
        aggregatedResult._metadata = {
          totalLoads: totalLoads,
          totalLocations: Object.keys(aggregatedResult).length
        };
        
        setAggregatedData(aggregatedResult);
      } else if (viewType === 'state' && geoJsonData) {
        addStateData(mapInstanceRef.current, geoJsonData);
        // aggregatedData is already set by fetchAggregatedData
      } else if (viewType === 'county' && geoJsonData) {
        addCountyData(mapInstanceRef.current, geoJsonData);
        // aggregatedData is already set by fetchAggregatedData
      } else if (viewType === 'city' && results && results.length > 0) {
        addCityData(mapInstanceRef.current, results);
        addRouteLines(mapInstanceRef.current, results);
        // For city view, create a simple aggregation
        const aggregatedResult = results.reduce((acc, load, index) => {
          acc[`load-${index}`] = {
            id: load.ID || `load-${index}`,
            origin: `${load.OriginCity}, ${load.OriginState}`,
            destination: `${load.DestinationCity}, ${load.DestinationState}`,
            equipment: load.Equipment,
            rate: load.RatePerMile,
            payment: load.PaymentAmount,
            mileage: load.Mileage,
            pickupDate: load.PickupDate
          };
          return acc;
        }, {});
        setAggregatedData(aggregatedResult);
      }
    }
  }, [results, mapLoaded, viewType, showRoutes, geoJsonData]);

  if (!isVisible) return null;

  return (
    <div className="space-y-4">
      {/* Map - Standalone without frame */}
      <div className="relative">
        <div 
          ref={mapRef} 
          className="w-full h-[600px] rounded-xl shadow-lg overflow-hidden"
          style={{ 
            minHeight: '500px',
            backgroundColor: '#f0f0f0',
            position: 'relative'
          }}
        />
        
        {/* State/County metrics box - positioned relative to map */}
        {selectedCounty && (
          <div className="absolute top-4 left-4 z-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 max-w-sm border border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {selectedCounty.name}{selectedCounty.state ? `, ${selectedCounty.state}` : ''}
                </h3>
                {selectedCounty.fips && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">FIPS: {selectedCounty.fips}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedCounty(null)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Total Loads:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedCounty.loadCount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Market Size:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  ${Math.round(selectedCounty.totalPayment).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Avg Payment:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  ${Math.round(selectedCounty.avgPayment)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Market Size:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  ${Math.round(selectedCounty.totalPayment).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Avg Mileage:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedCounty.avgMileage.toFixed(0)} mi
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Avg Rate:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  ${selectedCounty.avgRate.toFixed(2)}/mi
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Show Routes Toggle for City View */}
        {viewType === 'city' && (
          <div className="absolute top-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-600">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={showRoutes}
                onChange={(e) => setShowRoutes(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              />
              <span className="font-medium text-gray-900 dark:text-white">Show Routes</span>
            </label>
          </div>
        )}
        
        {(!mapLoaded || isLoading) && (
          <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center rounded-xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isLoading ? 'Loading aggregated data...' : 'Loading map...'}
              </p>
            </div>
          </div>
        )}
        
        {(!process.env.REACT_APP_MAPBOX_PUBLIC_KEY || !process.env.REACT_APP_MAPBOX_PUBLIC_KEY.startsWith('pk.')) && (
          <div className="absolute inset-0 bg-gray-100 dark:bg-gray-700 flex items-center justify-center rounded-xl">
            <div className="text-center p-4">
              <MapPin className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {!process.env.REACT_APP_MAPBOX_PUBLIC_KEY 
                  ? 'Mapbox API key required for map view'
                  : 'Mapbox GL requires a public token (pk.*), not secret token (sk.*)'
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Data Validation Section */}
      {aggregatedData && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 mt-6">
          <button
            onClick={() => setShowDataValidation(!showDataValidation)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-t-xl"
          >
            <div className="flex items-center space-x-3">
              <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <div className="flex flex-col">
                <span className="font-semibold text-gray-900 dark:text-white text-lg">
                  Data Validation - Aggregated Map Data
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {viewType === 'city' ? (
                    `${Object.keys(aggregatedData).length} loads`
                  ) : (
                    aggregatedData._metadata ? 
                      `${aggregatedData._metadata.totalLoads.toLocaleString()} total loads across ${aggregatedData._metadata.totalLocations} ${viewType === 'county' ? 'counties' : viewType === 'state' ? 'states' : 'regions'}` :
                      `${Object.keys(aggregatedData).filter(key => key !== '_metadata').length} ${viewType === 'county' ? 'counties' : viewType === 'state' ? 'states' : 'regions'}`
                  )}
                </span>
              </div>
            </div>
            {showDataValidation ? (
              <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            )}
          </button>
          
          {showDataValidation && (
            <div className="border-t border-gray-200 p-4">
              <div className="text-sm text-gray-600 mb-3">
                This data shows the aggregated values used to generate the map visualization for the selected view type: <strong>{viewType}</strong>
              </div>
              
              <div className="max-h-[600px] overflow-y-auto">
                {viewType === 'city' ? (
                  // City view - show individual loads
                  <div className="space-y-2">
                    {Object.values(aggregatedData).slice(0, 50).map((load, index) => (
                      <div key={load.id} className="bg-gray-50 rounded p-3 text-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div><strong>ID:</strong> {load.id}</div>
                          <div><strong>Origin:</strong> {load.origin}</div>
                          <div><strong>Destination:</strong> {load.destination}</div>
                          <div><strong>Equipment:</strong> {load.equipment}</div>
                          <div><strong>Rate/Mile:</strong> ${(load.rate || 0).toFixed(2)}</div>
                          <div><strong>Payment:</strong> ${(load.payment || 0).toLocaleString()}</div>
                          <div><strong>Mileage:</strong> {(load.mileage || 0).toLocaleString()} mi</div>
                          <div><strong>Date:</strong> {load.pickupDate || 'N/A'}</div>
                        </div>
                      </div>
                    ))}
                    {Object.keys(aggregatedData).length > 50 && (
                      <div className="text-center text-gray-500 text-sm py-2">
                        Showing first 50 of {Object.keys(aggregatedData).length} loads
                      </div>
                    )}
                  </div>
                ) : (
                  // Geographic views - show aggregated data
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {viewType === 'region' ? 'Region' : viewType === 'county' ? 'County FIPS' : 'State'}
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Loads</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Rate/Mile</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Payment</th>
                          {(viewType === 'state' || viewType === 'county') && (
                            <>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Origin Loads</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Dest Loads</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Market Size</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.values(aggregatedData).filter(item => item && typeof item === 'object' && !item.hasOwnProperty('totalLoads') && !item.hasOwnProperty('totalLocations')).slice(0, 100).map((location, index) => (
                          <tr key={location.fips || location.state || location.region || index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {location.region || location.state || location.fips || 'Unknown'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-right">
                              {(location.totalLoads || location.load_count || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-right">
                              ${(location.avgRate || location.avg_rate || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-right">
                              ${(location.avgPayment || location.avg_payment || 0).toLocaleString()}
                            </td>
                            {(viewType === 'state' || viewType === 'county') && (
                              <>
                                <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                  {(location.originLoads || 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                  {(location.destinationLoads || 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                  ${(location.totalRevenue || location.total_payment || 0).toLocaleString()}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {Object.keys(aggregatedData).filter(key => key !== '_metadata').length > 100 && (
                      <div className="text-center text-gray-500 text-sm py-2 bg-gray-50">
                        Showing first 100 of {Object.keys(aggregatedData).filter(key => key !== '_metadata').length} {viewType === 'region' ? 'regions' : viewType === 'county' ? 'counties' : 'states'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MapResults;